import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | undefined | null): string {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}

export function formatDateTime(date: string | Date | undefined | null): string {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

export function getRoleColor(role: string): string {
  switch (role) {
    // System roles
    case 'SUPER_ADMIN':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'PROJECT_ADMIN':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'MODERATOR':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    // Team roles (displayed to users)
    case 'owner':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'admin':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'editor':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'viewer':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

/** Capitalize first letter of a team role for display (e.g. "editor" → "Editor") */
export function formatTeamRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'ACTIVE':
    case 'confirmed':
    case 'replied':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'INACTIVE':
    case 'pending':
    case 'read':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'SUSPENDED':
    case 'ARCHIVED':
    case 'unsubscribed':
    case 'archived':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    case 'new':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'spam':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

/**
 * Get the URL to launch a project website.
 * In development, uses the project's localDevUrl if configured.
 * In production: legacy `domain` (singular) → `domains[0]` (the canonical
 * customer-facing hostname) → `sitesUrl` (the free <slug>.sites.tyashin.com
 * subdomain every project gets) → localDevUrl.
 */
export function getProjectUrl(project: {
  slug: string;
  domain?: string;
  domains?: string[];
  sitesUrl?: string | null;
  localDevUrl?: string;
}): string | null {
  const isDev = import.meta.env.DEV;

  // Helper to add protocol
  const addProtocol = (url: string, forceHttp = false): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // In dev mode or if it looks like a local URL, use http
    const isLocalUrl =
      url.includes('localhost') ||
      url.includes('127.0.0.1') ||
      url.includes('.local') ||
      url.match(/^192\.168\./) ||
      url.match(/^10\./) ||
      url.match(/:\d+/); // Has port number (likely local)

    if (forceHttp || isDev || isLocalUrl) {
      return `http://${url}`;
    }

    return `https://${url}`;
  };

  // In development, prefer localDevUrl
  if (isDev && project.localDevUrl) {
    return addProtocol(project.localDevUrl, true); // Force http in dev
  }

  // Use configured domain (legacy singular field, kept for back-compat)
  if (project.domain) {
    return addProtocol(project.domain, isDev); // Force http if in dev mode
  }

  // Canonical custom hostname — first entry of the connected-domains list.
  if (project.domains?.[0]) {
    return addProtocol(project.domains[0], isDev);
  }

  // Free Tyashin subdomain every project gets (<slug>.sites.tyashin.com).
  if (project.sitesUrl) {
    return addProtocol(project.sitesUrl, isDev);
  }

  // Fallback to localDevUrl even in production
  if (project.localDevUrl) {
    return addProtocol(project.localDevUrl, true);
  }

  return null;
}
